import {useCallback, use} from 'react';

import {ConfirmationContext} from './ConfirmationContext';
import {ConfirmConfig} from './state';

// Static class to handle confirmation resolution outside of React components
// Unexpected reassignment of a variable which was defined outside of the component.
// Components and hooks should be pure and side-effect free, but variable reassignment
// is a form of side-effect. If this variable is used in rendering, use useState instead.
// (https://react.dev/reference/rules/components-and-hooks-must-be-pure#side-effects-must-run-outside-of-render) [react-compiler/react-compiler]
class ConfirmationResolver {
  private static resolver: ((value: unknown) => void) | null = null;

  static setResolver<V>(res: (value: V) => void) {
    this.resolver = res as (value: unknown) => void;
  }

  static resolve<V>(value: V) {
    if (this.resolver) {
      this.resolver(value);
      this.resolver = null;
    }
  }
}

export type ConfirmFn = (config: string | ConfirmConfig) => Promise<boolean>;

export function useConfirm() {
  const {dispatch} = use(ConfirmationContext);

  const closeConfirm = useCallback(() => {
    dispatch({type: 'CLOSE_CONFIRM'});
  }, [dispatch]);

  const accept = useCallback(() => {
    closeConfirm();
    ConfirmationResolver.resolve(true);
  }, [closeConfirm]);

  const cancel = useCallback(() => {
    closeConfirm();
    ConfirmationResolver.resolve(false);
  }, [closeConfirm]);

  const confirm = useCallback(
    <T = boolean,>(payload: string | ConfirmConfig): Promise<T> => {
      dispatch({
        type: 'OPEN_CONFIRM',
        payload: typeof payload === 'string' ? {description: payload} : payload,
      });

      return new Promise<T>((res) => {
        ConfirmationResolver.setResolver<T>(res);
      });
    },
    [dispatch],
  );

  const confirmAction = useCallback(
    async (
      payload: string | ConfirmConfig,
      cond: boolean,
      onAccept: () => void | Promise<void>,
      onReject?: () => void | Promise<void>,
    ) => {
      if (!cond) {
        return onReject?.();
      }

      const confirmed = await confirm(payload);
      if (confirmed) {
        return onAccept();
      }
      return onReject?.();
    },
    [confirm],
  );

  const resolveValue = useCallback(
    <T,>(value: T, closeAfter: boolean) => {
      if (closeAfter) {
        closeConfirm();
      }
      ConfirmationResolver.resolve<T>(value);
    },
    [closeConfirm],
  );

  return {confirm, accept, cancel, closeConfirm, confirmAction, resolveValue};
}
